import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Label } from '@/components/ui/label';
import { InputOTP, InputOTPGroup, InputOTPSlot } from '@/components/ui/input-otp';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { KeyRound, Mail, Eye, EyeOff } from 'lucide-react';
import { Helmet } from 'react-helmet-async';
import { trackEvent } from '@/components/GoogleAnalytics';

const Auth = () => {
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [fullName, setFullName] = useState('');
  const [resetEmail, setResetEmail] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loginAttempts, setLoginAttempts] = useState(0);
  const [lastAttemptTime, setLastAttemptTime] = useState<number | null>(null);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { toast } = useToast();
  
  // Read tab from URL parameter, default to 'signin'
  const tabFromUrl = searchParams.get('tab') || 'signin';
  const [activeTab, setActiveTab] = useState(tabFromUrl);
  const hasTabParam = searchParams.has('tab'); // Check if coming from header buttons
  const [showOtpInput, setShowOtpInput] = useState(false);
  const [otpCode, setOtpCode] = useState('');
  const [otpEmail, setOtpEmail] = useState('');
  
  const returnTo = searchParams.get('returnTo') || '/';
  const shareToken = searchParams.get('shareToken'); // Check if coming from shared link

  // Rate limiting: max 5 attempts per 15 minutes
  const MAX_ATTEMPTS = 5;
  const RATE_LIMIT_WINDOW = 15 * 60 * 1000; // 15 minutes

  const isRateLimited = () => {
    if (!lastAttemptTime || loginAttempts < MAX_ATTEMPTS) return false;
    const timeSinceLastAttempt = Date.now() - lastAttemptTime;
    return timeSinceLastAttempt < RATE_LIMIT_WINDOW;
  };

  const recordFailedAttempt = () => {
    const now = Date.now();
    if (!lastAttemptTime || now - lastAttemptTime > RATE_LIMIT_WINDOW) {
      setLoginAttempts(1);
    } else {
      setLoginAttempts(prev => prev + 1);
    }
    setLastAttemptTime(now);
  };

  const resetAttempts = () => {
    setLoginAttempts(0);
    setLastAttemptTime(null);
  };

  // Enhanced input sanitization
  const sanitizeInput = (input: string) => {
    return input.trim().replace(/[<>]/g, '');
  };

  const validatePassword = (password: string) => {
    const minLength = 12; // Increased from 8
    const hasUpperCase = /[A-Z]/.test(password);
    const hasLowerCase = /[a-z]/.test(password);
    const hasNumbers = /\d/.test(password);
    const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(password);
    
    if (password.length < minLength) {
      return `Password must be at least ${minLength} characters long`;
    }
    if (!hasUpperCase) {
      return 'Password must contain at least one uppercase letter';
    }
    if (!hasLowerCase) {
      return 'Password must contain at least one lowercase letter';
    }
    if (!hasNumbers) {
      return 'Password must contain at least one number';
    }
    if (!hasSpecialChar) {
      return 'Password must contain at least one special character';
    }
    return null;
  };

  const resendOtp = async () => {
    setLoading(true);
    
    const { error } = await supabase.auth.signInWithOtp({
      email: otpEmail,
      options: {
        shouldCreateUser: true,
        emailRedirectTo: undefined, // Explicitly disable redirect
        data: {
          full_name: fullName,
        }
      }
    });

    if (error) {
      toast({
        title: "Resend failed",
        description: error.message,
        variant: "destructive"
      });
    } else {
      toast({
        title: "Code sent",
        description: "We've sent you a new verification code.",
      });
    }
    setLoading(false);
  };

  useEffect(() => {
    // Check if user is already logged in
    const checkUser = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (session) {
        navigate(returnTo);
      }
    };
    checkUser();
  }, [navigate, returnTo]);

  const handleSignUp = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    trackEvent('signup_started', { method: 'email' });

    try {
      // Sanitize inputs
      const sanitizedEmail = sanitizeInput(email);
      const sanitizedFullName = sanitizeInput(fullName);
      
      // Validate password
      const passwordError = validatePassword(password);
      if (passwordError) {
        toast({
          title: "Invalid password",
          description: passwordError,
          variant: "destructive"
        });
        setLoading(false);
        return;
      }
      
      // Check password confirmation
      if (password !== confirmPassword) {
        toast({
          title: "Passwords don't match",
          description: "Please make sure both passwords are the same",
          variant: "destructive"
        });
        setLoading(false);
        return;
      }
      
      // Check if email already exists
      const { data: emailExists } = await supabase.rpc('check_email_exists', {
        user_email: sanitizedEmail
      });
      
      if (emailExists) {
        toast({
          title: "Account already exists",
          description: "An account with this email already exists. Please sign in instead.",
          variant: "destructive"
        });
        setActiveTab('signin');
        setLoading(false);
        return;
      }
      
      const { error } = await supabase.auth.signUp({
        email: sanitizedEmail,
        password: password,
        options: {
          emailRedirectTo: `${window.location.origin}/auth`,
          data: {
            full_name: sanitizedFullName,
          }
        }
      });

      if (error) {
        toast({
          title: "Sign up failed",
          description: error.message,
          variant: "destructive"
        });
        setLoading(false);
        return;
      }

      // Show OTP input UI
      setShowOtpInput(true);
      setOtpEmail(sanitizedEmail);
      
      // Clear form fields except email for reference
      setPassword('');
      setConfirmPassword('');
      
      toast({
        title: "Code sent",
        description: "We've sent a 6-digit verification code to your email.",
      });
      
    } catch (error: any) {
      toast({
        title: "Sign up failed", 
        description: error.message || "An error occurred during sign up",
        variant: "destructive"
      });
    }
    
    setLoading(false);
  };

  const handleVerifyOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    if (otpCode.length !== 6) {
      toast({
        title: "Invalid code",
        description: "Please enter a valid 6-digit code.",
        variant: "destructive"
      });
      return;
    }

    setLoading(true);

    const { error } = await supabase.auth.verifyOtp({
      email: otpEmail,
      token: otpCode,
      type: 'email'
    });

    if (error) {
      toast({
        title: "Verification failed",
        description: error.message,
        variant: "destructive"
      });
      setLoading(false);
      return;
    }

    trackEvent('signup_completed', { method: 'email' });

    toast({
      title: "Welcome!",
      description: "Your account has been verified successfully.",
    });
    
    // Navigate to the appropriate destination
    const destination = shareToken 
      ? `/shared/${shareToken}`
      : returnTo;
    
    navigate(destination);
    setLoading(false);
  };

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    const { error } = await supabase.auth.resetPasswordForEmail(resetEmail, {
      redirectTo: `${window.location.origin}/reset-password`,
    });

    if (error) {
      toast({
        title: "Reset failed",
        description: error.message,
        variant: "destructive"
      });
    } else {
      toast({
        title: "Check your email",
        description: "We've sent you a password reset link.",
      });
    }
    setLoading(false);
  };

  const handleSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    
    trackEvent('login_started', { method: 'password' });
    
    // Check rate limiting
    if (isRateLimited()) {
      const remainingTime = Math.ceil((RATE_LIMIT_WINDOW - (Date.now() - (lastAttemptTime || 0))) / 60000);
      toast({
        title: "Too many attempts",
        description: `Please wait ${remainingTime} minutes before trying again.`,
        variant: "destructive"
      });
      return;
    }

    setLoading(true);

    // Sanitize inputs
    const sanitizedEmail = sanitizeInput(email);
    const sanitizedPassword = password; // Don't sanitize password as it may contain special chars

    const { data, error } = await supabase.auth.signInWithPassword({
      email: sanitizedEmail,
      password: sanitizedPassword,
    });

    if (error) {
      recordFailedAttempt();
      toast({
        title: "Sign in failed",
        description: "Invalid email or password. Please try again.",
        variant: "destructive"
      });
    } else {
      // For shared link users, allow unverified accounts
      if (shareToken && !data.user?.email_confirmed_at) {
        toast({
          title: "Welcome!",
          description: "You can use this shared checklist now. Consider verifying your email for full account features.",
        });
        navigate(returnTo);
        setLoading(false);
        return;
      }
      
      // Check if email is verified for regular users
      if (!shareToken && !data.user?.email_confirmed_at) {
        // Sign out the user immediately
        await supabase.auth.signOut();
        toast({
          title: "Email verification required",
          description: "Please verify your email address before signing in. Check your inbox for the confirmation link.",
          variant: "destructive"
        });
        setLoading(false);
        return;
      }
      
      resetAttempts();
      trackEvent('login_completed', { method: 'password' });
      toast({
        title: "Welcome back!",
        description: "You've been signed in successfully.",
      });
      navigate(returnTo);
    }
    setLoading(false);
  };

  return (
    <div className="relative min-h-screen flex items-center justify-center p-4">
      {/* Subtle branded background */}
      <div className="absolute inset-0">
        <img 
          src="/triathlon-finish-background.jpg"
          alt=""
          className="w-full h-full object-cover"
          loading="lazy"
        />
        <div className="absolute inset-0" style={{ backgroundColor: 'hsl(var(--background) / 0.80)' }} />
      </div>
      
      <Helmet>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>
      <Card className="relative w-full max-w-md shadow-modal">
        <CardHeader className="text-center">
          <CardTitle className="text-3xl font-display font-bold tracking-tight text-foreground">
            SINK BIKE RUN
          </CardTitle>
          <CardDescription className="mt-2 font-body">
            Train Hard. Pack Smart. Race Confident.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            {!hasTabParam && (
              <TabsList className="grid w-full grid-cols-3">
                <TabsTrigger value="signin">Sign In</TabsTrigger>
                <TabsTrigger value="signup">Sign Up</TabsTrigger>
                <TabsTrigger value="reset">Reset Password</TabsTrigger>
              </TabsList>
            )}
            
            <TabsContent value="signin">
              <form onSubmit={handleSignIn} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="signin-email">Email</Label>
                  <Input
                    id="signin-email"
                    type="email"
                    placeholder="your@email.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="signin-password">Password</Label>
                  <div className="relative">
                    <Input
                      id="signin-password"
                      type={showPassword ? "text" : "password"}
                      placeholder="Your password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      className="pr-10"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                      aria-label={showPassword ? "Hide password" : "Show password"}
                    >
                      {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                </div>
                <Button type="submit" className="w-full" disabled={loading}>
                  {loading ? "Signing in..." : "Sign In"}
                </Button>
                <div className="flex flex-col items-center gap-2 mt-2">
                  <Button 
                    type="button"
                    variant="link" 
                    onClick={() => setActiveTab('reset')}
                    className="text-sm text-muted-foreground hover:text-primary"
                  >
                    Forgot Password?
                  </Button>
                  {hasTabParam && (
                    <p className="text-sm text-muted-foreground">
                      Don't have an account?{' '}
                      <button
                        type="button"
                        onClick={() => {
                          setActiveTab('signup');
                          navigate('/auth?tab=signup');
                        }}
                        className="text-primary hover:underline font-medium"
                      >
                        Sign up
                      </button>
                    </p>
                  )}
                </div>
              </form>
            </TabsContent>
            
            <TabsContent value="signup">
              {showOtpInput ? (
                <div className="space-y-4 text-center">
                  <div className="space-y-2">
                    <h3 className="text-lg font-display font-semibold">Enter Verification Code</h3>
                    <p className="text-sm text-muted-foreground font-body leading-[1.7]">
                      We've sent a 6-digit code to <strong>{otpEmail}</strong>
                    </p>
                  </div>
                  
                  <form onSubmit={handleVerifyOtp} className="space-y-4">
                    <div className="flex justify-center">
                      <Label htmlFor="otp-code" className="sr-only">Verification Code</Label>
                      <InputOTP 
                        value={otpCode} 
                        onChange={(value) => setOtpCode(value)}
                        maxLength={6}
                      >
                        <InputOTPGroup>
                          <InputOTPSlot index={0} />
                          <InputOTPSlot index={1} />
                          <InputOTPSlot index={2} />
                          <InputOTPSlot index={3} />
                          <InputOTPSlot index={4} />
                          <InputOTPSlot index={5} />
                        </InputOTPGroup>
                      </InputOTP>
                    </div>
                    
                    <Button type="submit" className="w-full" disabled={loading || otpCode.length !== 6}>
                      {loading ? "Verifying..." : "Verify Code"}
                    </Button>
                  </form>
                  
                  <div className="space-y-3 text-sm text-muted-foreground">
                    {shareToken ? (
                      <div className="bg-primary/5 dark:bg-primary/10 border border-primary/20 dark:border-primary/30 rounded-lg p-3">
                        <p className="font-medium text-primary dark:text-primary/90">
                          After verification, you'll access the shared checklist
                        </p>
                        <p className="text-primary/80 dark:text-primary/70 mt-1">
                          You'll be able to save it to your account and customize it.
                        </p>
                      </div>
                    ) : (
                      <p>Enter the code to activate your account and start creating checklists.</p>
                    )}
                  </div>
                  
                  <div className="space-y-2">
                    <Button 
                      variant="outline" 
                      onClick={resendOtp}
                      disabled={loading}
                      className="w-full"
                    >
                      {loading ? "Sending..." : "Resend Code"}
                    </Button>
                  </div>
                </div>
              ) : (
                <form onSubmit={handleSignUp} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="signup-name">Full Name</Label>
                    <Input
                      id="signup-name"
                      type="text"
                      placeholder="Your full name"
                      value={fullName}
                      onChange={(e) => setFullName(e.target.value)}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="signup-email">Email</Label>
                    <Input
                      id="signup-email"
                      type="email"
                      placeholder="your@email.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                    />
                  </div>
                   <div className="space-y-2">
                     <Label htmlFor="signup-password">Password</Label>
                     <div className="relative">
                       <Input
                         id="signup-password"
                         type={showPassword ? "text" : "password"}
                         placeholder="Create a strong password"
                         value={password}
                         onChange={(e) => setPassword(e.target.value)}
                         required
                         minLength={12}
                         className="pr-10"
                       />
                       <button
                         type="button"
                         onClick={() => setShowPassword(!showPassword)}
                         className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                         aria-label={showPassword ? "Hide password" : "Show password"}
                       >
                         {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                       </button>
                     </div>
                     <div className="text-xs text-muted-foreground space-y-1">
                       <p>Password must contain:</p>
                       <ul className="list-disc list-inside space-y-0.5">
                         <li>At least 12 characters</li>
                         <li>One uppercase letter</li>
                         <li>One lowercase letter</li>
                         <li>One number</li>
                         <li>One special character (!@#$%^&*...)</li>
                       </ul>
                     </div>
                   </div>
                  <div className="space-y-2">
                    <Label htmlFor="signup-confirm">Confirm Password</Label>
                    <div className="relative">
                      <Input
                        id="signup-confirm"
                        type={showConfirmPassword ? "text" : "password"}
                        placeholder="Confirm your password"
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        required
                        minLength={8}
                        className="pr-10"
                      />
                      <button
                        type="button"
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                        aria-label={showConfirmPassword ? "Hide password" : "Show password"}
                      >
                        {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </button>
                    </div>
                  </div>
                  <Button type="submit" className="w-full" disabled={loading}>
                    {loading ? "Creating account..." : "Create Account"}
                  </Button>
                  {hasTabParam && (
                    <p className="text-sm text-muted-foreground text-center mt-4">
                      Already have an account?{' '}
                      <button
                        type="button"
                        onClick={() => {
                          setActiveTab('signin');
                          navigate('/auth?tab=signin');
                        }}
                        className="text-primary hover:underline font-medium"
                      >
                        Sign in
                      </button>
                    </p>
                  )}
                </form>
              )}
            </TabsContent>

            <TabsContent value="reset">
              <form onSubmit={handleForgotPassword} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="reset-email">Email</Label>
                  <Input
                    id="reset-email"
                    type="email"
                    placeholder="your@email.com"
                    value={resetEmail}
                    onChange={(e) => setResetEmail(e.target.value)}
                    required
                  />
                </div>
                <p className="text-sm text-muted-foreground">
                  Enter your email address and we'll send you a link to reset your password.
                </p>
                <Button type="submit" className="w-full" disabled={loading}>
                  {loading ? "Sending reset link..." : "Send Reset Link"}
                </Button>
              </form>
            </TabsContent>
          </Tabs>
          
          {!showOtpInput && (
            <div className="mt-6 text-center">
              <Button variant="ghost" onClick={() => navigate('/')}>
                Back to Home
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default Auth;